import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CacheService } from '@services/cache';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { ApplicationType } from '@shared/enums';
import { from, Observable, of } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

export interface CacheInput {
  cacheExpirationTime: number;
  forcedKey?: string;
}

export const IS_CACHE_ENABLED = new HttpContextToken<boolean>(() => false);
export const CACHE = new HttpContextToken<CacheInput>(() => ({ cacheExpirationTime: 0 }));

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  readonly #cacheService = inject(CacheService);
  readonly #contextService = inject(ContextService);
  readonly #logsService = inject(LogsService);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.#contextService.isServer()) return next.handle(req);
    if (req.context.get(IS_CACHE_ENABLED)) {
      const cache = req.context.get(CACHE);
      const key = cache.forcedKey ? cache.forcedKey : req.urlWithParams;
      return from(
        this.#cacheService.get(key).catch((error: unknown) => {
          this.#logsService.error('CacheInterceptor', 'Cache read failed, falling back to network', error);
          return null; // cache miss -> next.handle(req) takes over
        })
      ).pipe(
        mergeMap((value: unknown) => {
          if (!value) {
            return next.handle(req).pipe(
              tap((valueLoaded) => {
                if (valueLoaded instanceof HttpResponse) {
                  // warn is not forwarded to GCP in prod (logLevel ERROR), keeps the trace in test/local
                  this.#cacheService
                    .set(key, valueLoaded.body, cache.cacheExpirationTime)
                    .catch((error: unknown) => this.#logsService.warn('CacheInterceptor', 'Cache write failed', error));
                }
              })
            );
          }
          const cachedResponse: HttpResponse<unknown> = new HttpResponse<unknown>({ body: value });
          if (environment.applicationType === ApplicationType.LOCAL) {
            this.#logsService.debug('CacheInterceptor', `${req.method} ${req.url} (cache hit)`, { muted: true });
          }
          return of(cachedResponse.clone());
        })
      );
    }
    return next.handle(req);
  }
}
